import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  Dashboard,
  AccountBalance,
  TrendingUp,
  Today,
  ShoppingCart,
  Assignment,
  Gavel,
  ExpandMore,
  AccountTree,
  BookOnline,
  MenuBook,
  Assessment,
  Receipt,
  MonetizationOn,
  People,
  LocalShipping,
  Description,
  Payment,
  BarChart,
  Lightbulb,
  RequestQuote,
  AssignmentReturn,
  EventNote,
  Group,
  AttachMoney,
  Timer,
  Inventory,
  Warehouse,
  SwapHoriz,
  ViewList,
  AdminPanelSettings,
  Timeline,
  ManageAccounts,
  Badge,
  AccessTime,
  BeachAccess,
  Payments,
  WorkOutline,
  Handshake,
  PersonSearch,
  FactCheck,
  Inventory2,
  Rule,
  Factory,
  PrecisionManufacturing,
  Calculate,
  Summarize,
  Extension,
  Bolt,
  VpnKey,
  ImportExport,
  AutoAwesomeMosaic,
  CompareArrows,
  AccountBalanceWallet,
  Savings,
  CreditScore,
  CurrencyExchange,
  Public,
  WarningAmber,
  PlaylistAddCheck,
  Insights,
  ReceiptLong,
  Schema,
  Route,
  Analytics,
  Handyman,
  Build,
  Outbox,
  Tune,
  ExitToApp,
  Engineering,
  UploadFile,
  CalendarMonth,
  Campaign,
  EmojiEvents,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppTheme } from '../../theme';
import { useAuth } from '../../context/AuthContext';
import { useModules } from '../../context/ModulesContext';
import { getSubdomain } from '../../utils/subdomain';
import Wordmark from './Wordmark';

interface SidebarProps {
  open: boolean;
  handleDrawerToggle: () => void;
  drawerWidth: number;
}

interface SubMenuItem {
  text: string;
  path: string;
  icon: React.ReactElement;
  /**
   * Catalogue navItem key (moduleCatalogue.js). Present on relocatable items so
   * a tenant nav override can find and move them between sections.
   */
  key?: string;
}

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  subItems?: SubMenuItem[];
}

interface MenuItemWithRole extends MenuItem {
  roles?: string[];
  /**
   * Eyebrow printed above this item. Purely a visual grouping — the item
   * order and routes are unchanged, so nothing about the IA moves.
   */
  section?: string;
  /**
   * The à-la-carte module this item belongs to (moduleCatalogue.js key). When
   * set, the item is hidden unless the tenant has that module enabled. Absent
   * = always shown (core sections: Dashboard, Reports, Administration).
   */
  moduleKey?: string;
}

const allMenuItems: MenuItemWithRole[] = [
  {
    text: 'Command Center',
    icon: <PrecisionManufacturing />,
    path: '/command-center',
    section: 'Overview',
  },
  {
    text: 'Dashboard',
    icon: <Dashboard />,
    path: '/dashboard',
    section: 'Overview',
  },
  {
    text: 'Reports',
    icon: <Assessment />,
    path: '/reports',
  },
  {
    text: 'Administration',
    icon: <AdminPanelSettings />,
    path: '/admin',
    roles: ['SUPER_ADMIN'],
    subItems: [
      { text: 'Activity Dashboard', path: '/admin/dashboard', icon: <Timeline /> },
      { text: 'User Management', path: '/admin/users', icon: <ManageAccounts /> },
    ],
  },
  {
    text: 'Finance',
    icon: <AccountBalance />,
    path: '/finance',
    section: 'Operations',
    moduleKey: 'finance',
    subItems: [
      { text: 'Command Center', path: '/finance', icon: <Dashboard /> },
      // General Ledger
      { text: 'Journal Entries', path: '/finance/journal', icon: <BookOnline /> },
      { text: 'General Ledger', path: '/finance/ledger', icon: <MenuBook /> },
      { text: 'Chart of Accounts', path: '/finance/accounts', icon: <AccountTree /> },
      { text: 'Recurring Journals', path: '/finance/recurring-journals', icon: <Timeline /> },
      // Receivables / Payables
      { text: 'Credit / Debit Notes', path: '/finance/credit-debit-notes', icon: <Receipt /> },
      { text: 'Collections', path: '/finance/collections', icon: <MonetizationOn /> },
      { text: 'AR / AP Ageing', path: '/finance/ageing', icon: <Assessment /> },
      // Taxation
      { text: 'GST', path: '/finance/gst-reports', icon: <Gavel /> },
      { text: 'TDS', path: '/finance/tds-reports', icon: <MonetizationOn /> },
      // Budgeting & Costing
      { text: 'Budgets', path: '/finance/budgets', icon: <Calculate /> },
      { text: 'Cost Centers', path: '/finance/cost-centers', icon: <AttachMoney /> },
      // Period Close
      { text: 'Period Close', path: '/finance/period-close', icon: <Rule /> },
      // Reports
      { text: 'Financial Reports', path: '/finance/reports', icon: <Assessment /> },
      { text: 'Day Book', path: '/finance/day-book', icon: <MenuBook /> },
      { text: 'Bank Reconciliation', path: '/finance/bank-reconciliation', icon: <Rule /> },
      { text: 'Expenses', path: '/finance/expenses', icon: <RequestQuote /> },
      { text: 'Bill Allocation', path: '/finance/payment-allocations', icon: <AccountBalanceWallet /> },
      { text: 'Bank Import', path: '/finance/bank-statements', icon: <UploadFile /> },
    ],
  },
  {
    text: 'Fixed Assets',
    icon: <Factory />,
    path: '/assets',
    roles: ['SUPER_ADMIN', 'ADMIN', 'PROCUREMENT', 'FINANCE'],
    moduleKey: 'fixed_assets',
    subItems: [
      { text: 'Asset Register', path: '/assets', icon: <Inventory2 /> },
      { text: 'Depreciation Workbench', path: '/assets/depreciation', icon: <Calculate /> },
      { text: 'IT Blocks', path: '/assets/blocks', icon: <AccountTree /> },
      { text: 'Reports', path: '/assets/reports', icon: <Summarize /> },
    ],
  },
  {
    text: 'Treasury',
    icon: <AccountBalanceWallet />,
    path: '/treasury',
    roles: ['SUPER_ADMIN', 'ADMIN', 'FINANCE'],
    moduleKey: 'treasury',
    subItems: [
      { text: 'Treasury Dashboard', path: '/treasury', icon: <Dashboard /> },
      { text: 'Executive MIS', path: '/treasury/executive-mis', icon: <Assessment /> },
      { text: 'Cash Management', path: '/treasury/cash', icon: <AccountBalanceWallet /> },
      { text: 'Bank Accounts', path: '/treasury/banks', icon: <AccountBalance /> },
      { text: 'Reconciliation', path: '/treasury/reconciliation', icon: <Rule /> },
      { text: 'Payments', path: '/treasury/payments', icon: <Payment /> },
      { text: 'Receivables & Payables', path: '/treasury/ar-ap', icon: <SwapHoriz /> },
      { text: 'Investments', path: '/treasury/investments', icon: <Savings /> },
      { text: 'Loans & Borrowings', path: '/treasury/loans', icon: <CreditScore /> },
      { text: 'Working Capital', path: '/treasury/working-capital', icon: <Assessment /> },
      { text: 'FX Rates', path: '/treasury/fx', icon: <CurrencyExchange /> },
      { text: 'Risk & Compliance', path: '/treasury/risk', icon: <Gavel /> },
    ],
  },
  {
    text: 'Import / Export',
    icon: <Public />,
    path: '/exim',
    roles: ['SUPER_ADMIN', 'ADMIN', 'FINANCE', 'SALES', 'PROCUREMENT'],
    moduleKey: 'exim',
  },
  {
    text: 'HR & People',
    icon: <Badge />,
    path: '/hr',
    moduleKey: 'hr_payroll',
    subItems: [
      { text: 'Command Center', path: '/hr', icon: <Dashboard /> },
      { text: 'Employees', path: '/hr/employees', icon: <People /> },
      { text: 'Org Chart', path: '/hr/org-chart', icon: <AccountTree /> },
      { text: 'Recruitment', path: '/hr/recruitment-ats', icon: <WorkOutline /> },
      { text: 'My Day', path: '/hr/my-day', icon: <Today /> },
    ],
  },
  {
    text: 'Time & Payroll',
    icon: <AccessTime />,
    path: '/hr/time-payroll',
    moduleKey: 'hr_payroll',
    subItems: [
      { text: 'Command Center', path: '/hr/time-payroll', icon: <Dashboard /> },
      { text: 'Attendance Control', path: '/hr/attendance-control', icon: <Insights /> },
      { text: 'Attendance (Daily)', path: '/hr/attendance', icon: <AccessTime /> },
      { text: 'Shifts & Regularization', path: '/hr/shifts', icon: <CalendarMonth /> },
      { text: 'Leave Control', path: '/hr/leave-control', icon: <FactCheck /> },
      { text: 'Leave Requests', path: '/hr/leave', icon: <BeachAccess /> },
      { text: 'Payroll', path: '/hr/payroll', icon: <Payments />, key: 'hr.payroll' },
      { text: 'Salary Structures', path: '/hr/salary-structures', icon: <MonetizationOn /> },
    ],
  },
  {
    text: 'Talent & Lifecycle',
    icon: <EmojiEvents />,
    path: '/hr/performance',
    moduleKey: 'hr_payroll',
    subItems: [
      { text: 'Performance', path: '/hr/performance', icon: <Assessment /> },
      { text: 'Learning', path: '/hr/learning', icon: <MenuBook /> },
      { text: 'Lifecycle', path: '/hr/lifecycle', icon: <ExitToApp /> },
      { text: 'HR Analytics', path: '/hr/analytics', icon: <Insights /> },
    ],
  },
  {
    text: 'CRM',
    icon: <Handshake />,
    path: '/crm',
    moduleKey: 'sales_crm',
    subItems: [
      { text: 'Command Center', path: '/crm', icon: <Dashboard /> },
      { text: 'Leads', path: '/crm/leads', icon: <PersonSearch /> },
      { text: 'Pipeline', path: '/crm/pipeline', icon: <Timeline /> },
      { text: 'Activities', path: '/crm/activities', icon: <EventNote /> },
      { text: 'My Day', path: '/crm/my-day', icon: <Today /> },
      { text: 'Tasks', path: '/crm/tasks', icon: <Assignment /> },
      { text: 'Calendar', path: '/crm/calendar', icon: <CalendarMonth /> },
    ],
  },
  {
    text: 'Revenue Ops',
    icon: <Insights />,
    path: '/crm/leaderboard',
    moduleKey: 'sales_crm',
    subItems: [
      { text: 'Forecast', path: '/crm/forecast', icon: <Insights /> },
      { text: 'Forecast Submission', path: '/crm/forecast-submission', icon: <Summarize /> },
      { text: 'Leaderboard', path: '/crm/leaderboard', icon: <EmojiEvents /> },
      { text: 'Territories', path: '/crm/territories', icon: <Public /> },
      { text: 'Win / Loss', path: '/crm/win-loss', icon: <Assessment /> },
      { text: 'Campaigns', path: '/crm/campaigns', icon: <Campaign /> },
      { text: 'Approvals', path: '/crm/approvals', icon: <FactCheck /> },
    ],
  },
  {
    text: 'Sales',
    icon: <TrendingUp />,
    path: '/sales',
    moduleKey: 'sales_crm',
    subItems: [
      { text: 'Products', path: '/sales/products', icon: <Inventory /> },
      { text: 'Customers', path: '/sales/customers', icon: <People /> },
      { text: 'Opportunities', path: '/sales/opportunities', icon: <Lightbulb /> },
      { text: 'Quotations', path: '/sales/quotations', icon: <RequestQuote /> },
      { text: 'Sales Orders', path: '/sales/orders', icon: <Description /> },
      { text: 'Enquiries', path: '/sales/enquiries', icon: <PersonSearch /> },
      { text: 'Contracts', path: '/sales/contracts', icon: <Gavel /> },
      { text: 'Deliveries', path: '/sales/deliveries', icon: <LocalShipping /> },
      { text: 'Invoices', path: '/sales/invoices', icon: <Receipt /> },
      { text: 'Sales Returns', path: '/sales/returns', icon: <AssignmentReturn /> },
      { text: 'Sales Reports', path: '/sales/reports', icon: <BarChart /> },
    ],
  },
  {
    text: 'Procurement',
    icon: <ShoppingCart />,
    path: '/procurement',
    moduleKey: 'procurement',
    subItems: [
      { text: 'Command Center', path: '/procurement', icon: <Dashboard /> },
      { text: 'Requisitions', path: '/procurement/requisitions', icon: <PlaylistAddCheck /> },
      { text: 'RFQ / Sourcing', path: '/procurement/rfqs', icon: <CompareArrows /> },
      { text: 'Purchase Orders', path: '/procurement/orders', icon: <Description /> },
      { text: 'Goods Receipts', path: '/procurement/goods-receipts', icon: <Inventory2 /> },
      { text: 'Service Entries', path: '/procurement/service-entries', icon: <ReceiptLong /> },
      { text: 'Three-Way Match', path: '/procurement/match', icon: <Rule /> },
      { text: 'Purchase Invoices', path: '/procurement/invoices', icon: <Receipt /> },
      { text: 'Vendor Payments', path: '/procurement/payments', icon: <Payment /> },
      { text: 'Service Work Orders', path: '/procurement/service-work-orders', icon: <Engineering /> },
      { text: 'Service Bill Passing', path: '/procurement/service-bill-passing', icon: <FactCheck /> },
      { text: 'Payment Proposal', path: '/procurement/payments/proposal', icon: <AccountBalanceWallet /> },
      { text: 'Exceptions', path: '/procurement/exceptions', icon: <WarningAmber /> },
      { text: 'Analytics', path: '/procurement/analytics', icon: <Insights /> },
      { text: 'Vendors', path: '/procurement/vendors', icon: <LocalShipping /> },
      { text: 'Vendor Onboarding', path: '/procurement/vendor-onboarding', icon: <FactCheck /> },
      { text: 'TDS Management', path: '/procurement/tds', icon: <MonetizationOn />, key: 'proc.tds' },
      { text: 'Reports', path: '/procurement/reports', icon: <BarChart /> },
    ],
  },
  {
    text: 'Projects',
    icon: <Assignment />,
    path: '/projects',
    moduleKey: 'projects',
    subItems: [
      { text: 'Command Center', path: '/projects', icon: <Dashboard /> },
      { text: 'Portfolio', path: '/projects/portfolio', icon: <ViewList /> },
      { text: 'All Projects', path: '/projects/all', icon: <EventNote /> },
      { text: 'My Day', path: '/projects/my-day', icon: <Today /> },
      { text: 'Tasks', path: '/projects/tasks', icon: <Assignment /> },
      { text: 'Resources', path: '/projects/resources', icon: <Group /> },
      { text: 'Cost Tracking', path: '/projects/costs', icon: <AttachMoney /> },
      { text: 'Time Tracking', path: '/projects/time', icon: <Timer /> },
      { text: 'Project Reports', path: '/projects/reports', icon: <BarChart /> },
    ],
  },
  {
    text: 'Inventory',
    icon: <Inventory />,
    path: '/inventory',
    moduleKey: 'inventory',
    subItems: [
      { text: 'Command Center', path: '/inventory', icon: <Dashboard /> },
      { text: 'Stock List', path: '/inventory/stock', icon: <ViewList /> },
      { text: 'Stock Movements', path: '/inventory/movements', icon: <SwapHoriz /> },
      { text: 'Warehouses', path: '/inventory/warehouses', icon: <Warehouse /> },
      { text: 'Reorder Planning', path: '/inventory/reorder', icon: <PlaylistAddCheck /> },
      { text: 'Expiry', path: '/inventory/expiry', icon: <WarningAmber /> },
      { text: 'Serial Numbers', path: '/inventory/serials', icon: <Inventory2 /> },
      { text: 'Analytics', path: '/inventory/analytics', icon: <Analytics /> },
    ],
  },
  {
    text: 'SCM / Stores',
    icon: <Warehouse />,
    path: '/scm',
    moduleKey: 'inventory',
    subItems: [
      { text: 'Material Requisitions', path: '/scm/material-requisitions', icon: <Assignment /> },
      { text: 'Material Issues', path: '/scm/material-issues', icon: <Outbox /> },
      { text: 'Stock Adjustments', path: '/scm/stock-adjustments', icon: <Tune /> },
      { text: 'Stock Transfers', path: '/scm/stock-transfers', icon: <SwapHoriz /> },
      { text: 'Stock Verification', path: '/scm/stock-counts', icon: <FactCheck /> },
      { text: 'Tools & Tackles', path: '/scm/tools', icon: <Build /> },
      { text: 'Gate Pass', path: '/scm/gate-passes', icon: <ExitToApp /> },
      { text: 'Reason Codes', path: '/scm/reason-codes', icon: <Rule /> },
    ],
  },
  {
    text: 'Manufacturing',
    icon: <PrecisionManufacturing />,
    path: '/manufacturing',
    moduleKey: 'manufacturing',
    subItems: [
      // Planning
      { text: 'Production Control', path: '/manufacturing', icon: <Assessment /> },
      { text: 'MRP / Planning', path: '/manufacturing/mrp', icon: <Analytics /> },
      // Engineering
      { text: 'BOM', path: '/manufacturing/bom', icon: <Schema /> },
      { text: 'Routing', path: '/manufacturing/routing', icon: <Route /> },
      // Execution
      { text: 'Work Centers', path: '/manufacturing/work-centers', icon: <Factory /> },
      { text: 'Production Orders', path: '/manufacturing/work-orders', icon: <Description /> },
      // Quality
      { text: 'Quality Control', path: '/manufacturing/quality', icon: <FactCheck /> },
      { text: 'Inspection Lots', path: '/manufacturing/quality/lots', icon: <Rule /> },
      { text: 'NCR / CAPA', path: '/manufacturing/quality/ncr', icon: <WarningAmber /> },
      { text: 'QC Parameters', path: '/manufacturing/qc-parameters', icon: <Tune /> },
      { text: 'Item QC Specs', path: '/manufacturing/qc-item-parameters', icon: <Rule /> },
      // Maintenance
      { text: 'Equipment', path: '/manufacturing/maintenance/equipment', icon: <Handyman /> },
      { text: 'PM Schedules', path: '/manufacturing/maintenance/schedules', icon: <EventNote /> },
      { text: 'Maintenance Orders', path: '/manufacturing/maintenance/work-orders', icon: <Build /> },
      // Performance
      { text: 'OEE & Downtime', path: '/manufacturing/oee', icon: <Timeline /> },
    ],
  },
  {
    text: 'Compliance',
    icon: <Gavel />,
    path: '/compliance',
    section: 'Governance',
    moduleKey: 'compliance',
  },
  {
    text: 'Workflows',
    icon: <AccountTree />,
    path: '/workflows',
    roles: ['SUPER_ADMIN', 'ADMIN'],
    moduleKey: 'workflows',
  },
  {
    text: 'Integrations',
    icon: <Extension />,
    path: '/integrations',
    roles: ['SUPER_ADMIN', 'ADMIN'],
    moduleKey: 'integrations',
    subItems: [
      { text: 'Catalogue', path: '/integrations', icon: <Extension /> },
      { text: 'Connections', path: '/integrations/connections', icon: <SwapHoriz /> },
      { text: 'Webhooks', path: '/integrations/webhooks', icon: <Bolt /> },
      { text: 'API Keys', path: '/integrations/api-keys', icon: <VpnKey /> },
      { text: 'Import / Export', path: '/integrations/import-export', icon: <ImportExport /> },
    ],
  },
  {
    // The marketplace itself. Core platform tooling for the org owner — never
    // gated by an entitlement, only by role.
    text: 'Plan & Modules',
    icon: <AutoAwesomeMosaic />,
    path: '/admin/modules',
    roles: ['SUPER_ADMIN', 'ADMIN'],
  },
];

/** Mono eyebrow, matching the landing page's section labels. */
const eyebrowSx = {
  fontFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: '0.625rem',
  fontWeight: 500,
  letterSpacing: '0.16em',
  textTransform: 'uppercase' as const,
};

const Sidebar: React.FC<SidebarProps> = ({ open, handleDrawerToggle, drawerWidth }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { colors, brand } = useAppTheme();
  const { user } = useAuth();
  const { isModuleEnabled, navOverrides } = useModules();
  const [expandedItems, setExpandedItems] = React.useState<string[]>([]);

  const workspace = React.useMemo(() => getSubdomain(), []);

  // Filter menu items by role AND by the tenant's enabled modules. An item
  // with a moduleKey the tenant hasn't purchased is hidden here — the single
  // choke-point — and its routes are separately blocked by <ModuleGuard>.
  const filteredItems = React.useMemo(() => {
    return allMenuItems.filter(item => {
      if (item.roles && !(user?.role && item.roles.includes(user.role))) return false;
      if (item.moduleKey && !isModuleEnabled(item.moduleKey)) return false;
      return true;
    });
  }, [user?.role, isModuleEnabled]);

  // Apply the tenant's relocation overrides (Phase 2): pull a relocatable
  // sub-item out of its home section and re-home it under the target section.
  // A move whose destination isn't currently visible (module off / role-hidden)
  // is skipped, so the item never vanishes. The item's route is unchanged — only
  // where it is *listed* moves.
  const menuItems = React.useMemo(() => {
    const entries = Object.entries(navOverrides || {});
    if (entries.length === 0) return filteredItems;

    // Clone items with fresh subItems arrays so we never mutate allMenuItems.
    const items = filteredItems.map((it) => ({
      ...it,
      subItems: it.subItems ? [...it.subItems] : it.subItems,
    }));
    const byPath = new Map(items.map((it) => [it.path, it]));

    for (const [navKey, targetPath] of entries) {
      const target = byPath.get(targetPath);
      if (!target) continue; // destination section not visible right now
      let moved: SubMenuItem | null = null;
      for (const it of items) {
        if (!it.subItems) continue;
        const idx = it.subItems.findIndex((s) => s.key === navKey);
        if (idx >= 0) {
          moved = it.subItems[idx];
          it.subItems.splice(idx, 1);
          break;
        }
      }
      if (!moved) continue;
      if (!target.subItems) target.subItems = [];
      if (!target.subItems.some((s) => s.key === navKey)) target.subItems.push(moved);
    }
    return items;
  }, [filteredItems, navOverrides]);

  React.useEffect(() => {
    // Auto-expand a parent when the active route is under its path OR is one of
    // its (possibly relocated) sub-items — a payroll item moved under Finance
    // must expand Finance, whose path /finance the /hr/payroll route doesn't
    // share.
    menuItems.forEach((item) => {
      if (
        item.subItems &&
        (location.pathname.startsWith(item.path) ||
          item.subItems.some((s) => location.pathname === s.path))
      ) {
        setExpandedItems((prev) =>
          prev.includes(item.text) ? prev : [...prev, item.text]
        );
      }
    });
  }, [location.pathname, menuItems]);

  const handleNavigation = (path: string) => {
    navigate(path);
    // Matches AppLayout's `sm` breakpoint. This previously hardcoded 900px,
    // so between 600 and 900 the persistent desktop drawer closed itself on
    // every navigation.
    if (window.innerWidth < 600) {
      handleDrawerToggle();
    }
  };

  const handleExpandClick = (itemText: string) => {
    setExpandedItems((prev) =>
      prev.includes(itemText)
        ? prev.filter((i) => i !== itemText)
        : [...prev, itemText]
    );
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isExpanded = (itemText: string) => expandedItems.includes(itemText);

  const focusRing = {
    '&:focus-visible': {
      outline: `2px solid ${colors.sidebarAccent}`,
      outlineOffset: '-2px',
    },
  };

  const drawerStyles = {
    '& .MuiDrawer-paper': {
      boxSizing: 'border-box',
      width: drawerWidth,
      background: colors.sidebarBgGradient,
      // A hairline instead of the previous `4px 0 20px` drop shadow. On a dark
      // panel there is nothing for a shadow to darken, so it reads as smudge
      // rather than elevation; separation comes from the border.
      borderRight: '1px solid rgba(255,255,255,0.08)',
      boxShadow: 'none',
      color: colors.sidebarText,
      overflow: 'hidden',
    },
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* Masked grid — the same motif as the auth panel, faded out before it
          reaches the nav items so it never competes with the labels. */}
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse 120% 40% at 50% 0%, #000 0%, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(ellipse 120% 40% at 50% 0%, #000 0%, transparent 75%)',
          pointerEvents: 'none',
        }}
      />

      {/* Brand */}
      <Box sx={{ position: 'relative', px: 2.5, pt: 3, pb: 2.5 }}>
        <Box
          role="button"
          tabIndex={0}
          onClick={() => handleNavigation('/dashboard')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleNavigation('/dashboard');
            }
          }}
          sx={{
            display: 'inline-flex',
            cursor: 'pointer',
            borderRadius: 1.5,
            ...focusRing,
          }}
        >
          {/* Inline SVG rather than the 192px PWA raster, which softens badly
              at this size. */}
          <Wordmark size={30} tone="dark" />
        </Box>

        {/* Which workspace this is. Only rendered on a real tenant host —
            inventing a label on localhost would be worse than showing none. */}
        {workspace && (
          <Tooltip title="Current workspace" placement="right">
            <Box
              sx={{
                mt: 2,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.875,
                maxWidth: '100%',
                px: 1.25,
                py: 0.625,
                borderRadius: 999,
                background: 'linear-gradient(180deg, rgba(255,255,255,0.055), rgba(255,255,255,0.02))',
                border: '1px solid rgba(255,255,255,0.12)',
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  flexShrink: 0,
                  backgroundColor: colors.sidebarAccent,
                  boxShadow: `0 0 8px ${colors.sidebarAccent}`,
                }}
              />
              <Typography
                sx={{
                  ...eyebrowSx,
                  color: colors.sidebarText,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {workspace}
              </Typography>
            </Box>
          </Tooltip>
        )}
      </Box>

      {/* Navigation */}
      <List
        sx={{
          position: 'relative',
          px: 1.25,
          pb: 2,
          flexGrow: 1,
          overflowY: 'auto',
          // Thin, self-coloured scrollbar. The platform default is a bright
          // slab on a near-black panel.
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255,255,255,0.18) transparent',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255,255,255,0.18)',
            borderRadius: 3,
          },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
        }}
      >
        {menuItems.map((item) => {
          const hasSubItems = item.subItems && item.subItems.length > 0;
          const itemActive = isActive(item.path);
          const itemExpanded = isExpanded(item.text);

          return (
            <React.Fragment key={item.text}>
              {item.section && (
                <Typography
                  component="li"
                  sx={{
                    ...eyebrowSx,
                    color: colors.sidebarTextMuted,
                    // No additional `opacity` here on purpose. The muted token
                    // is already semi-transparent in every preset, so layering
                    // CSS opacity on top compounds the two and drops 10px text
                    // below AA. The eyebrow is distinguished by the mono face,
                    // size and tracking instead.
                    listStyle: 'none',
                    px: 1.75,
                    pt: 2.25,
                    pb: 1,
                  }}
                >
                  {item.section}
                </Typography>
              )}

              <ListItem disablePadding sx={{ mb: 0.25 }}>
                <ListItemButton
                  onClick={() => {
                    if (hasSubItems) {
                      handleExpandClick(item.text);
                    } else {
                      handleNavigation(item.path);
                    }
                  }}
                  aria-expanded={hasSubItems ? itemExpanded : undefined}
                  sx={{
                    position: 'relative',
                    borderRadius: 2,
                    py: 1.05,
                    pl: 2,
                    pr: 1.5,
                    color: itemActive ? colors.sidebarText : colors.sidebarTextMuted,
                    backgroundColor: itemActive ? colors.sidebarActiveItem : 'transparent',
                    transition: 'background-color .18s ease, color .18s ease',
                    ...focusRing,
                    // The active rail. A gradient bar pinned to the left edge
                    // reads as "you are here" at a glance in a way a flat
                    // background tint does not, and it carries the brand ramp
                    // into the app shell.
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 3,
                      height: itemActive ? '60%' : 0,
                      borderRadius: '0 3px 3px 0',
                      background: colors.sidebarAccentGradient,
                      transition: 'height .2s cubic-bezier(.16,1,.3,1)',
                      '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                    },
                    '&:hover': {
                      backgroundColor: itemActive
                        ? colors.sidebarActiveItem
                        : colors.sidebarHoverItem,
                      color: colors.sidebarText,
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: itemActive ? colors.sidebarAccent : 'inherit',
                      minWidth: 38,
                      transition: 'color .18s ease',
                      '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: itemActive ? 600 : 500,
                      fontSize: '0.875rem',
                      letterSpacing: '-0.01em',
                    }}
                  />
                  {hasSubItems && (
                    <ExpandMore
                      sx={{
                        fontSize: '1.1rem',
                        opacity: 0.5,
                        transform: itemExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform .2s ease',
                        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>

              {hasSubItems && (
                <Collapse in={itemExpanded} timeout="auto" unmountOnExit>
                  {/* Connector spine: ties the children to their parent so a
                      six-item submenu doesn't read as six more top-level rows. */}
                  <List
                    component="div"
                    disablePadding
                    sx={{
                      position: 'relative',
                      ml: 3.25,
                      pl: 1.5,
                      my: 0.5,
                      borderLeft: '1px solid rgba(255,255,255,0.1)',
                    }}
                  >
                    {item.subItems!.map((subItem) => {
                      const subItemActive = location.pathname === subItem.path;
                      return (
                        <ListItem key={subItem.text} disablePadding>
                          <ListItemButton
                            onClick={() => handleNavigation(subItem.path)}
                            sx={{
                              position: 'relative',
                              py: 0.7,
                              pl: 1.5,
                              pr: 1.5,
                              borderRadius: 1.5,
                              color: subItemActive
                                ? colors.sidebarText
                                : colors.sidebarTextMuted,
                              backgroundColor: subItemActive
                                ? colors.sidebarActiveItem
                                : 'transparent',
                              transition: 'background-color .18s ease, color .18s ease',
                              ...focusRing,
                              // Node on the spine, so the active child is
                              // legible even when scrolled away from its parent.
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: -13,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: 5,
                                height: 5,
                                borderRadius: '50%',
                                backgroundColor: subItemActive
                                  ? colors.sidebarAccent
                                  : 'transparent',
                                boxShadow: subItemActive
                                  ? `0 0 6px ${colors.sidebarAccent}`
                                  : 'none',
                                transition: 'background-color .18s ease',
                              },
                              '&:hover': {
                                backgroundColor: subItemActive
                                  ? colors.sidebarActiveItem
                                  : colors.sidebarHoverItem,
                                color: colors.sidebarText,
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 30,
                                color: subItemActive ? colors.sidebarAccent : 'inherit',
                                transition: 'color .18s ease',
                                '& .MuiSvgIcon-root': { fontSize: '1rem' },
                              }}
                            >
                              {subItem.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={subItem.text}
                              primaryTypographyProps={{
                                fontSize: '0.8125rem',
                                fontWeight: subItemActive ? 600 : 400,
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>

      {/* Footer */}
      <Box
        sx={{
          position: 'relative',
          px: 2.5,
          py: 1.75,
          borderTop: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Typography sx={{ ...eyebrowSx, color: colors.sidebarTextMuted }}>
          {brand.shortName}
        </Typography>
        <Typography
          sx={{
            fontSize: '0.6875rem',
            color: colors.sidebarTextMuted,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          v{brand.version}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { sm: open ? drawerWidth : 0 },
        flexShrink: 0,
        transition: 'width 0.2s ease',
      }}
      aria-label="navigation menu"
    >
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={open}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          ...drawerStyles,
        }}
      >
        {drawer}
      </Drawer>
      {/* Desktop drawer */}
      <Drawer
        variant="persistent"
        open={open}
        sx={{
          display: { xs: 'none', sm: 'block' },
          ...drawerStyles,
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
